import LocationOffIcon from "@mui/icons-material/LocationOff";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import { Box, Stack, Typography } from "@mui/material";

import { QuoteScorePanel } from "components/Carrier/components/QuoteScorePanel";
import { QuoteEquipmentDisplay } from "components/QuoteEquipmentDisplay";
import { QuoteStatusBadge } from "components/QuoteStatusBadge";
import { ShipperViewedIndicator } from "components/ShipperViewedIndicator";
import { useRoleContext } from "hooks";
import {
  getNoteLabel,
  getQuoteSenderLabel,
  getQuoteTitle,
  isDeadQuote,
  ISOStringToLocalReadableTimestamp,
  ISOStringToReadableNotLocalized,
} from "utils";

interface Props {
  quote: CondensedQuote;
}

interface ShipmentEtaDetailsProps {
  quote: CondensedQuote;
}

function ShipmentEtaDetails(props: ShipmentEtaDetailsProps) {
  const { quote } = props;
  const isMultiShipmentProject = quote?.quote_type === "MULTI_SHIPMENT_PROJECT";
  const pickupStartLabel = isMultiShipmentProject
    ? "Delivery ETA"
    : "Pickup ETA";
  const deliveryEtaLabel = isMultiShipmentProject
    ? "Last Pickup ETA"
    : "Delivery ETA";

  return (
    <>
      <Typography>
        <b>{`${pickupStartLabel}: `}</b>
        {quote?.pickup_start
          ? ISOStringToReadableNotLocalized(quote.pickup_start)
          : "-"}
      </Typography>
      <Typography>
        <b>{`${deliveryEtaLabel}: `}</b>
        {quote?.delivery_eta
          ? ISOStringToReadableNotLocalized(quote.delivery_eta)
          : "-"}
      </Typography>
    </>
  );
}

export function CarrierQuoteDetail(props: Props) {
  const { quote } = props;
  const { role } = useRoleContext();

  const isMultiShipmentProject = quote?.quote_type === "MULTI_SHIPMENT_PROJECT";
  const showEtaDetails = quote.shipment_type !== "QUOTES_ONLY";
  const isDead = isDeadQuote(quote.internal_state);
  // A dead quote's fields fade to grey — they can no longer be acted on.
  const deadFieldSx = isDead ? { opacity: 0.6 } : undefined;
  const deadTextColor = isDead ? "text.disabled" : undefined;

  return (
    <Stack direction="column" spacing={2} width="100%">
      <Stack direction="row" spacing={2} alignItems="center">
        <Typography variant="h4" color={deadTextColor}>
          {getQuoteTitle(quote)}
        </Typography>
        <QuoteStatusBadge
          status={quote.internal_state}
          size="medium"
          isHidden={quote.hidden_at != null}
        />
      </Stack>
      <QuoteScorePanel
        quoteId={quote.quote}
        internalState={quote.internal_state}
        lastActionAt={quote.created_at}
      />
      {!isDead && (
        <ShipperViewedIndicator
          quoteId={quote.quote}
          viewedAt={quote.last_viewed_by_shipper}
          sx={{ alignSelf: "flex-start" }}
        />
      )}
      <Typography variant="body1" color="#0c4985">
        {getQuoteSenderLabel(quote, role)}
      </Typography>
      {quote?.internal_state === "DECLINED" && quote?.decline_feedback && (
        <Typography>
          <b>Decline Reason: </b>
          {quote.decline_feedback}
        </Typography>
      )}
      {isMultiShipmentProject && (
        <>
          <Typography sx={{ textDecoration: "underline" }} fontWeight="bold">
            {`Minimum Loads: ${quote?.min_loads_desired ?? "-"}`}
          </Typography>
          <Typography sx={{ textDecoration: "underline" }} fontWeight="bold">
            {`Maximum Loads: ${quote?.max_loads_desired ?? "-"}`}
          </Typography>
        </>
      )}
      <Box sx={deadFieldSx}>
        <QuoteEquipmentDisplay equipmentType={quote?.equipment_type} />
      </Box>
      {showEtaDetails && (
        <Box sx={deadFieldSx}>
          <ShipmentEtaDetails quote={quote} />
          <Stack direction="row" alignItems="center">
            <Typography>
              <b>{"Tracking Link: "}</b>
              {quote?.tracking_link_offered ? "Yes" : "No"}
            </Typography>
            {quote.tracking_link_offered ? (
              <LocationOnIcon
                fontSize="small"
                color={isDead ? "disabled" : "success"}
              />
            ) : (
              <LocationOffIcon fontSize="small" />
            )}
          </Stack>
        </Box>
      )}
      <Typography
        variant="body1"
        color={deadTextColor}
        sx={{
          whiteSpace: "normal",
          overflow: "hidden",
          textOverflow: "ellipsis",
          wordBreak: "break-word",
          maxWidth: "100%",
        }}
      >
        <b>{`${getNoteLabel(role)} `}</b>
        {quote?.notes ?? ""}
      </Typography>
      <Typography
        variant="body1"
        color="text.secondary"
        sx={deadFieldSx}
      >{`Submited ${ISOStringToLocalReadableTimestamp(quote.created_at)}`}</Typography>
    </Stack>
  );
}
